var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import Base from "../Base.js";
import Events from "../mixin/Events.js";
import ObjectHelper from "../helper/ObjectHelper.js";
import StringHelper from "../helper/StringHelper.js";
import Localizable from "../localization/Localizable.js";
function getDescriptor(me, fnName) {
  const property = ObjectHelper.getPropertyDescriptor(me, fnName);
  return property && (property.get || property.set) ? property : null;
}
class InstancePlugin extends Base.mixin(Events, Localizable) {
  onConfigChange(...args) {
    var _a, _b;
    super.onConfigChange(...args);
    if (!this.isConfiguring) {
      (_b = (_a = this.client) == null ? void 0 : _a.afterPluginConfigChange) == null ? void 0 : _b.call(_a, { plugin: this, ...args[0] });
    }
  }
  //endregion
  updateClient(client) {
    if (!this.owner) {
      this.owner = client;
    }
  }
  /**
   * This will merge a feature's (subclass of InstancePlugin) keyMap with it's client's keyMap.
   * @private
   */
  updateKeyMap(keyMap) {
    const { client } = this;
    client.keyMap = client.mergeKeyMaps(client.keyMap, keyMap, StringHelper.uncapitalize(this.constructor.$name));
  }
  //region Init
  /**
   * Call from another instance to add plugins to it.
   *
   * ```javascript
   * InstancePlugin.initPlugins(this, Search, Stripe);
   * ```
   *
   * @param plugInto Instance to mix into (usually this)
   * @param plugins Classes to plug in
   * @internal
   */
  static initPlugins(plugInto, ...plugins) {
    for (const PluginClass of plugins) {
      this.initPlugin(plugInto, PluginClass);
    }
  }
  /**
   * Call from another instance to add a plugin to it
   *
   * @param plugInto Instance to mix into (usually this)
   * @param PluginClass Class to plug in
   * @param config Configuration object to pass to the plugin constructor
   * @returns {Core.mixin.InstancePlugin}
   * @internal
   */
  static initPlugin(plugInto, PluginClass, config) {
    const property = plugInto.plugins || (plugInto.plugins = {}), args = [plugInto];
    if (config) {
      args.push(config);
    }
    return property[PluginClass.$$name] = new PluginClass(...args);
  }
  /**
   * Simple wrapper for {@link #property-disabled} to make optional chaining simple:
   *
   * ```javascript
   * grid.features.myFeature?.enabled // returns true when feature exists and is enabled
   * ```
   * @returns {Boolean}
   * @internal
   */
  get enabled() {
    return !this.disabled;
  }
  // We can act as an owner of a widget, so must be able to participate in focus reversion
  getFocusRevertTarget() {
    var _a;
    return (_a = this.client) == null ? void 0 : _a.getFocusRevertTarget();
  }
  construct(...args) {
    const me = this;
    let [plugInto, config] = args, listeners;
    if (args.length === 1) {
      if (ObjectHelper.isObject(plugInto)) {
        config = plugInto;
        plugInto = config.client;
      }
    } else {
      config = ObjectHelper.assign({}, config);
      delete config.client;
    }
    me.client = plugInto;
    super.construct(config);
    me.applyPluginConfig(plugInto);
    listeners = me.clientListeners;
    if (listeners) {
      listeners = ObjectHelper.assign({}, listeners);
      listeners.thisObj = me;
      plugInto.ion(listeners);
    }
  }
  /**
   * Applies config as found in plugInto.pluginConfig, or published all if no config found.
   * @private
   * @param plugInto Target instance to plug into
   */
  applyPluginConfig(plugInto) {
    const me = this, config = me.pluginConfig || me.constructor.pluginConfig;
    if (config) {
      const { assign, chain, after, before, override } = config;
      assign && me.applyAssign(plugInto, assign);
      (chain || after) && me.applyChain(plugInto, chain || after);
      before && me.applyChain(plugInto, before, false);
      override && me.applyOverride(plugInto, override);
    }
  }
  /**
   * Applies assigning for specified functions.
   * @private
   * @param plugInto
   * @param fnNames
   */
  applyAssign(plugInto, fnNames) {
    fnNames.forEach((fnName) => this.assign(plugInto, fnName));
  }
  /**
   * Applies chaining for specified functions.
   * @private
   * @param plugInto
   * @param functions
   * @param after
   */
  applyChain(plugInto, functions, after = true) {
    if (Array.isArray(functions)) {
      for (const fnName of functions) {
        this.chain(plugInto, fnName, fnName, after);
      }
    } else {
      for (const intoName in functions) {
        this.chain(plugInto, intoName, functions[intoName], after);
      }
    }
  }
  /**
   * Applies override for specified functions.
   * @private
   * @param plugInto
   * @param fnNames
   */
  applyOverride(plugInto, fnNames) {
    const me = this;
    if (!me.overridden) {
      me.overridden = {};
    }
    fnNames.forEach((fnName) => {
      if (!me[fnName]) {
        throw new Error(`Trying to chain fn ${plugInto.$$name}#${fnName}, but plugin fn ${me.$$name}#${fnName} does not exist`);
      }
      if (typeof plugInto[fnName] === "function") {
        me.overridden[fnName] = plugInto[fnName].bind(plugInto);
      }
      plugInto[fnName] = me[fnName].bind(me);
    });
  }
  /**
   * Assigns specified functions.
   * @private
   * @param plugInto
   * @param fnName
   */
  assign(plugInto, fnName) {
    const me = this, property = getDescriptor(me, fnName);
    if (property) {
      Object.defineProperty(plugInto, fnName, {
        configurable: true,
        enumerable: true,
        get: property.get && property.get.bind(me),
        set: property.set && property.set.bind(me)
      });
    } else {
      plugInto[fnName] = me[fnName].bind(me);
    }
  }
  //endregion
  //region Chaining
  /**
   * Chains functions. When the function is called on the target class all functions in the chain will be called in
   * the order they were added.
   * @private
   * @param plugInto
   * @param intoName
   * @param hookName
   * @param after
   */
  chain(plugInto, intoName, hookName, after = true) {
    let prio = 0;
    if (typeof intoName === "object") {
      intoName = intoName.fn;
    }
    if (typeof hookName === "object") {
      prio = hookName.prio || 0;
      hookName = hookName.fn;
    }
    const me = this, chains = plugInto.pluginFunctionChain || (plugInto.pluginFunctionChain = {}), hookFn = me[hookName] && me[hookName].bind(me), functionChainRunner = me.functionChainRunner;
    if (!hookFn) {
      throw new Error(`Trying to chain fn ${plugInto.$$name}#${hookName}, but plugin fn ${me.$$name}#${hookName} does not exist`);
    }
    if (!chains[intoName]) {
      let intoFn = plugInto[intoName];
      if (intoFn) {
        intoFn = intoFn.bind(plugInto);
        intoFn.$this = plugInto;
        intoFn.$prio = 0;
      }
      chains[intoName] = intoFn ? [intoFn] : [];
      plugInto[intoName] = (...params) => functionChainRunner(chains[intoName], params);
    }
    hookFn.$this = me;
    hookFn.$prio = prio;
    chains[intoName][after ? "push" : "unshift"](hookFn);
    chains[intoName].$sorted = false;
  }
  /**
   * Used to run multiple plugged in functions with the same name, see chain above. Returning false from a
   * function will abort chain.
   * @private
   * @param {Array} chain
   * @param {Array} params
   * @returns {*} value returned from last function in chain (or false if any returns false)
   */
  functionChainRunner(chain, params) {
    let fn, i, returnValue;
    if (!chain.$sorted) {
      chain.sort((a, b) => b.$prio - a.$prio);
      chain.$sorted = true;
    }
    for (i = 0; i < chain.length; i++) {
      fn = chain[i];
      if (!fn.$this.isDestroyed) {
        returnValue = fn(...params);
        if (returnValue === false) {
          break;
        }
      }
    }
    return returnValue;
  }
  //endregion
  /**
   * Called when disabling/enabling the plugin/feature, not intended to be called directly. To enable or disable a
   * plugin/feature, see {@link #property-disabled}.
   *
   * By default removes the cls of the plugin from its client. Override in subclasses to take any other actions necessary.
   * @category Misc
   * @advanced
   */
  doDisable(disable) {
    var _a, _b, _c, _d;
    const me = this, { constructor } = me, cls = "featureClass" in constructor ? constructor.featureClass : `b-${constructor.$$name.toLowerCase()}`, key = StringHelper.uncapitalize(constructor.$$name);
    if (cls) {
      (_b = (_a = me.client) == null ? void 0 : _a._element) == null ? void 0 : _b.classList[disable ? "remove" : "add"](cls);
    }
    if (!me.isConfiguring) {
      if (disable) {
        me.trigger("disable");
      } else {
        me.trigger("enable");
      }
      (_d = (_c = me.client).syncSplits) == null ? void 0 : _d.call(_c, (other) => {
        const otherFeature = other.features[key];
        if (otherFeature) {
          otherFeature.disabled = disable;
        }
      });
    }
  }
  updateDisabled(disabled) {
    this.doDisable(disabled);
  }
  throwOverrideIsMissing(data) {
    throw new Error(`Trying to override fn ${data.plugIntoName}#${data.fnName}, but plugin fn ${data.pluginName}#${data.fnName} does not exist`);
  }
  // Convenience method to read the rootElement from the owner widget
  get rootElement() {
    return this.client.rootElement;
  }
  get state() {
    var _a;
    return (_a = this.constructor.stateProperties) == null ? void 0 : _a.reduce((acc, prop) => {
      acc[prop] = this[prop];
      return acc;
    }, {});
  }
}
__publicField(InstancePlugin, "$name", "InstancePlugin");
//region Config
__publicField(InstancePlugin, "configurable", {
  /**
   * The plugin/feature `disabled` state.
   *
   * For a feature that is **off** by default that you want to enable later during runtime,
   * configure it with `disabled : true`.
   * ```javascript
   * const grid = new Grid({
   *      features : {
   *          featureName : {
   *              disabled : true // on and disabled, can be enabled later
   *          }
   *      }
   * });
   *
   * // enable the feature
   * grid.features.featureName.disabled = false;
   * ```
   *
   * If the feature is **disabled** by default, and you want to include and enable the feature, configure it as `true`:
   * ```javascript
   * const grid = new Grid({
   *      features : {
   *          featureName : true // on and enabled, can be disabled later
   *      }
   * });
   *
   * // disable the feature
   * grid.features.featureName.disabled = true;
   * ```
   *
   * If the feature is **on** by default, but you want to turn it **off**, configure it as `false`:
   * ```javascript
   * const grid = new Grid({
   *      features : {
   *          featureName : false // turned off, not included at all
   *      }
   * });
   * ```
   *
   * If the feature is **enabled** by default and you have no need of reconfiguring it,
   * you can omit the feature configuration.
   *
   * @prp {Boolean}
   * @typings {Boolean|'inert'}
   * @default
   * @category Common
   */
  disabled: false,
  /**
   * The Widget which was passed into the constructor,
   * which is the Widget we are providing extra services for.
   * @member {Core.widget.Widget} client
   * @readonly
   * @category Misc
   * @advanced
   */
  /**
   * The widget which this plugin is to attach to.
   * @config {Core.widget.Widget}
   * @category Misc
   * @advanced
   */
  client: null,
  // The plugins can define their own keyMap which will then be merged with their client's keyMap.
  keyMap: null
  /**
   * @hideconfigs bubbleEvents, callOnFunctions
   */
  /**
   * @hidefunctions downloadTestCase, destroy
   */
  /**
   * @hideproperties isDestroyed
   */
  /**
   * @hideevents destroy, beforeDestroy
   */
});
__publicField(InstancePlugin, "properties", {
  clientListeners: null
});
InstancePlugin._$name = "InstancePlugin";
export {
  InstancePlugin as default
};
